import { ChevronRight, Focus, FolderTree, X } from "lucide-react";
import { useEffect, useState } from "react";
import type KpiModelNodeDetail from "@/_generated/types/serializers/KpiModelNodeDetail";
import { getIcon } from "@/lib/icons";
import { bizminer_admin_kpi_model_node_path } from "@/routes";

interface Props {
  nodeId: number | null;
  onClose: () => void;
  onNodeSelect?: (ancestorIds: number[], nodeId: number) => void;
  onFocus?: (ancestorIds: number[], nodeId: number) => void;
}

const LEVEL_COLORS: Record<number, string> = {
  1: "bg-chart-1/10 text-chart-1",
  2: "bg-chart-2/10 text-chart-2",
  3: "bg-chart-3/10 text-chart-3",
  4: "bg-chart-4/10 text-chart-4",
  5: "bg-chart-5/10 text-chart-5",
};

export default function NodeDetailPanel({
  nodeId,
  onClose,
  onNodeSelect,
  onFocus,
}: Props) {
  const [nodeData, setNodeData] = useState<KpiModelNodeDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!nodeId) {
      setNodeData(null);
      return;
    }

    const fetchNodeData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          bizminer_admin_kpi_model_node_path(nodeId),
        );
        if (response.ok) {
          const data: KpiModelNodeDetail = await response.json();
          setNodeData(data);
        } else {
          setError("Failed to load node details");
        }
      } catch (_err) {
        setError("Failed to load node details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchNodeData();
  }, [nodeId]);

  if (!nodeId) return null;

  return (
    <div className="absolute top-0 right-0 w-80 border-l border-border bg-card flex flex-col h-full shadow-lg z-10">
      <div className="flex items-start justify-between gap-2 p-4 border-b border-border">
        <div className="min-w-0 flex-1">
          {nodeData && (
            <>
              <h3 className="font-semibold text-card-foreground flex items-center gap-2">
                {(() => {
                  const Icon = getIcon(nodeData.icon);
                  return Icon ? <Icon className="h-5 w-5 shrink-0" /> : null;
                })()}
                <span>{nodeData.name}</span>
              </h3>
              <span
                className={`inline-block mt-1 px-1.5 py-0.5 rounded text-xs font-medium ${LEVEL_COLORS[nodeData.level]}`}
              >
                L{nodeData.level} {nodeData.level_name}
              </span>
              {nodeData.description && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {nodeData.description}
                </p>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          {nodeData && (
            <button
              type="button"
              onClick={() =>
                onFocus?.(
                  nodeData.ancestors.map((a) => a.id),
                  nodeId!,
                )
              }
              className="shrink-0 p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
              title="Focus on node"
            >
              <Focus className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {isLoading && (
          <div className="text-sm text-muted-foreground">Loading...</div>
        )}

        {error && <div className="text-sm text-destructive">{error}</div>}

        {nodeData && !isLoading && (
          <div className="space-y-6">
            {nodeData.ancestors.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  Path
                </h4>
                <div className="flex flex-wrap items-center gap-1">
                  {nodeData.ancestors.map((ancestor, index) => {
                    const ancestorIds = nodeData.ancestors
                      .slice(0, index)
                      .map((a) => a.id);
                    return (
                      <span key={ancestor.id} className="flex items-center">
                        <button
                          type="button"
                          onClick={() =>
                            onNodeSelect?.(ancestorIds, ancestor.id)
                          }
                          className={`px-1.5 py-0.5 rounded text-xs font-medium hover:ring-1 hover:ring-current transition-shadow ${LEVEL_COLORS[ancestor.level]}`}
                        >
                          {ancestor.name}
                        </button>
                        {index < nodeData.ancestors.length - 1 && (
                          <ChevronRight className="h-3 w-3 text-muted-foreground mx-0.5" />
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {nodeData.children_count > 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FolderTree className="h-4 w-4" />
                <span>{nodeData.children_count} children</span>
              </div>
            )}

            {nodeData.has_rubric && nodeData.criteria.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  Scoring Criteria
                </h4>
                <ol className="space-y-3">
                  {nodeData.criteria.map((criterion, index) => (
                    <li key={index} className="flex gap-3">
                      <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">
                        {index + 1}
                      </span>
                      <p className="text-sm text-card-foreground leading-relaxed">
                        {criterion}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {!nodeData.has_rubric && (
              <div className="text-sm text-muted-foreground italic">
                No rubric defined for this node
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
